import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import axios from "axios";
import useAuth from "src/hooks/useAuth";
import useSettings from "src/hooks/useSettings";
import { config } from "src/constants";
import useDuressAudio from "./useDuressAudio";
import { describeError, socketUrl, sortIncidents } from "./duressClient";

import { makeTestIncident, testAction, testInput } from "./testAlert";

const Context = createContext(null);
export const useDuress = () => useContext(Context);
export function DuressProvider({
  children,
  userOverride,
  presentationOnly = false,
}) {
  const auth = useAuth();
  const user = userOverride || auth.user;
  const { settings } = useSettings();
  const api = settings.api || config.api;
  const scope = `${api}:${user.username_id}:${user.companyid || "linked"}`;
  const [liveIncidents, setIncidents] = useState([]),
    [connected, setConnected] = useState(false),
    [error, setError] = useState("");
  const [testRecords, setTestRecords] = useState(null);
  const testing = Boolean(testRecords && !liveIncidents.length);
  const incidents = liveIncidents.length
    ? liveIncidents
    : (testRecords || []).filter((entry) => entry.status !== "resolved");
  const [revision, setRevision] = useState(0),
    [selectedId, setSelectedId] = useState(null),
    [showHistory, setShowHistory] = useState(false);
  const [pauseMotion, setPauseMotion] = useState(false),
    [loaded, setLoaded] = useState(false);
  const sequence = useRef(0),
    mounted = useRef(true),
    versions = useRef(new Map());
  const pendingRefresh = useRef(null),
    refreshAgain = useRef(false);
  const refresh = useCallback(async () => {
    if (presentationOnly) {
      setLoaded(true);
      return;
    }
    if (pendingRefresh.current) {
      refreshAgain.current = true;
      return pendingRefresh.current;
    }
    const request = ++sequence.current;
    const requestPromise = (async () => {
      try {
        const { data } = await axios.get(`${api}/v2/duress/incidents`, {
          timeout: 10000,
        });
        if (!mounted.current || request !== sequence.current) return;
        if (!Array.isArray(data.incidents))
          throw new Error("Invalid duress response");
        const updated = data.incidents
          .map((entry) => {
            const known = versions.current.get(entry.id);
            const newest =
              known && known.version > entry.version ? known : entry;
            versions.current.set(entry.id, newest);
            return newest;
          })
          .filter((entry) => entry.status !== "resolved");
        setIncidents(sortIncidents(updated));
        setError("");
        setLoaded(true);
        setRevision((value) => value + 1);
      } catch (failure) {
        if (mounted.current && request === sequence.current)
          setError(describeError(failure));
      }
    })();
    pendingRefresh.current = requestPromise;
    await requestPromise;
    pendingRefresh.current = null;
    if (mounted.current && refreshAgain.current) {
      refreshAgain.current = false;
      return refresh();
    }
  }, [api, presentationOnly]);
  const merge = useCallback((incident) => {
    ++sequence.current;
    versions.current.set(incident.id, incident);
    setIncidents((current) =>
      sortIncidents([
        ...current.filter((entry) => entry.id !== incident.id),
        ...(incident.status === "resolved" ? [] : [incident]),
      ])
    );
    setRevision((value) => value + 1);
  }, []);
  const startTest = useCallback(() => {
    if (!presentationOnly || liveIncidents.length) return;
    setTestRecords([makeTestIncident(user)]);
    setSelectedId(null);
    setShowHistory(false);
  }, [liveIncidents.length, user, presentationOnly]);
  const endTest = useCallback(() => {
    setTestRecords(null);
    setSelectedId(null);
  }, []);
  const releaseTest = useCallback(
    () =>
      setTestRecords((records) => records?.map((entry) => testInput(entry, 0))),
    []
  );
  const pressTest = useCallback(
    () =>
      setTestRecords((records) => {
        if (!records) return records;
        if (records.every((entry) => entry.status === "resolved")) {
          return [
            ...records,
            { ...makeTestIncident(user), id: `test-${records.length + 1}` },
          ];
        }
        return records.map((entry) => testInput(entry, 1));
      }),
    [user]
  );
  const getTestIncident = useCallback(
    (id) => testRecords?.find((entry) => entry.id === id),
    [testRecords]
  );
  const act = useCallback(
    async (incident, action, options = {}) => {
      if (typeof incident.id === "string" && incident.id.startsWith("test-")) {
        const updated = testAction(incident, action, user, options);
        setTestRecords((records) =>
          records.map((entry) => (entry.id === updated.id ? updated : entry))
        );
        setRevision((value) => value + 1);
        return updated;
      }
      try {
        const { data } = await axios.post(
          `${api}/v2/duress/incidents/${incident.id}/${action}`,
          { version: incident.version, ...options },
          { timeout: 10000 }
        );
        merge(data);
        refresh();
        return data;
      } catch (failure) {
        refresh();
        throw failure;
      }
    },
    [api, user, merge, refresh]
  );
  useEffect(() => {
    if (liveIncidents.length && testRecords) {
      setTestRecords(null);
      setSelectedId(null);
    }
  }, [liveIncidents.length, testRecords]);
  useEffect(() => {
    mounted.current = true;
    if (presentationOnly) {
      setLoaded(true);
      return undefined;
    }
    let socket,
      retry,
      watchdog,
      polling,
      stopped = false,
      online = false,
      lastMessage = 0,
      failures = 0;
    const fetchState = () => refresh();
    const send = (type, id, endpoint, data) =>
      socket?.readyState === 1 &&
      socket.send(JSON.stringify({ type, id, endpoint, data }));
    const connect = () => {
      if (stopped) return;
      try {
        socket = new WebSocket(socketUrl());
      } catch {
        retry = window.setTimeout(connect, 5000);
        return;
      }
      lastMessage = Date.now();
      socket.onopen = () =>
        send("auth", "duress-auth", "duress", {
          token: window.localStorage.getItem("accessToken"),
        });
      socket.onmessage = ({ data }) => {
        let message;
        try {
          message = JSON.parse(data);
        } catch {
          return;
        }
        lastMessage = Date.now();
        if (message.type === "error") {
          socket.close();
          return;
        }
        if (message.id === "duress-auth" && message.data?.success)
          send("subscribe", "duress-sub", "duress", {
            event: "duress.changed",
          });
        if (
          ["duress-sub", "duress-ping"].includes(message.id) &&
          message.type === "response"
        ) {
          const available = message.data?.live !== false;
          const changed = online !== available;
          online = available;
          failures = 0;
          setConnected(available);
          if (changed || message.id === "duress-sub") {
            window.clearTimeout(polling);
            poll();
          }
        }
        if (
          message.type === "event" &&
          message.data?.event === "duress.changed"
        )
          fetchState();
      };
      socket.onerror = () => socket.close();
      socket.onclose = () => {
        if (stopped) return;
        online = false;
        setConnected(false);
        window.clearTimeout(polling);
        poll();
        retry = window.setTimeout(
          connect,
          Math.min(1000 * 2 ** failures++, 15000)
        );
      };
    };
    const poll = () => {
      fetchState();
      polling = window.setTimeout(poll, online ? 30000 : 5000);
    };
    const visibility = () => {
      if (document.visibilityState === "visible") {
        fetchState();
        if (socket?.readyState === 1)
          send("request", "duress-ping", "duress.ping", {});
      }
    };
    watchdog = window.setInterval(() => {
      if (socket?.readyState === 1) {
        if (Date.now() - lastMessage > 45000) socket.close();
        else send("request", "duress-ping", "duress.ping", {});
      }
    }, 15000);
    poll();
    connect();
    const requested = new URLSearchParams(window.location.search).get("duress");
    if (/^\d+$/.test(requested || "")) setSelectedId(Number(requested));
    document.addEventListener("visibilitychange", visibility);
    window.addEventListener("online", visibility);
    return () => {
      stopped = true;
      mounted.current = false;
      ++sequence.current;
      window.clearTimeout(retry);
      window.clearTimeout(polling);
      window.clearInterval(watchdog);
      socket?.close();
      document.removeEventListener("visibilitychange", visibility);
      window.removeEventListener("online", visibility);
    };
  }, [api, refresh, presentationOnly]);
  const alarmKey = incidents
    .filter(
      (entry) => entry.status === "unacknowledged" && entry.is_test !== true
    )
    .map((entry) => entry.id)
    .sort()
    .join(",");
  const audio = useDuressAudio(alarmKey, scope);
  const value = useMemo(
    () => ({
      api,
      user,
      incidents,
      alarmKey,
      connected,
      error,
      loaded,
      revision,
      refresh,
      merge,
      selectedId,
      setSelectedId,
      showHistory,
      setShowHistory,
      pauseMotion,
      setPauseMotion,
      audio,
      act,
      testing,
      startTest: presentationOnly ? startTest : null,
      localTestMode: presentationOnly,
      endTest,
      releaseTest,
      pressTest,
      getTestIncident,
      testHistory: testRecords,
    }),
    [
      api,
      user,
      incidents,
      alarmKey,
      connected,
      error,
      loaded,
      revision,
      refresh,
      merge,
      selectedId,
      showHistory,
      pauseMotion,
      audio,
      act,
      testing,
      startTest,
      endTest,
      releaseTest,
      pressTest,
      getTestIncident,
      testRecords,
      presentationOnly,
    ]
  );
  return <Context.Provider value={value}>{children}</Context.Provider>;
}
